import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { analyzePaymentPlatformBankStatement, autofillPaymentPlatformAssessment, createPaymentPlatformAssessment, lookupPayoneerMerchants, scorePayoneerCnHkMerchant } from "../api/companyApi";

const STEPS = ["Business", "Application", "Performance", "Review"];
const PAYONEER_TENANT_ID = "RjISjKSKeFaagy0xxIaQ";
const INITIAL_FORM = {
  externalId: "", applicationId: "", companyName: "", registrationNumber: "",
  website: "", email: "", address: "", city: "", state: "", zip: "",
  requestedAmount: "", averageMonthlyVolume: "", businessMonths: "", fico: "", businessRisk: "",
  transactions: "", disputes: "", logins: "", productViews: "", feedbackScore: "", feedbackCount: "",
  entityConfidence: "", legitimacy: "", externalRisk: "", factorRate: "", remittanceRate: "",
};
const EMPTY_MONTH = { month: "", paymentVolume: "", transactions: "", disputes: "" };
const MONTHLY_METRICS = ["paymentVolume", "transactions", "disputes"];
const INITIAL_PAYONEER_INPUTS = {
  country: "CN", accountAgeDays: "", volume30d: "", volume90d: "", volume180d: "", volume270d: "",
  eligibleVolume90d: "", eligibleVolume180d: "", transactions90d: "", activeDays90d: "", activeMonths270d: "",
  loaderCount180d: "", amazonVolume90d: "", walmartVolume90d: "", ebayVolume90d: "", usdPsVolume90d: "",
  eurPsVolume90d: "", priorOffers: "", knownPriorDelinquencies: "",
};
const PAYONEER_VOLUME_FIELDS = [
  ["accountAgeDays", "Account age (days)"], ["volume30d", "Volume 30d (USD)"], ["volume90d", "Volume 90d (USD)"],
  ["volume180d", "Volume 180d (USD)"], ["volume270d", "Volume 270d (USD)"], ["eligibleVolume90d", "Eligible volume 90d (USD)"],
  ["eligibleVolume180d", "Eligible volume 180d (USD)"], ["transactions90d", "Transactions 90d"], ["activeDays90d", "Active days 90d"],
];
const PAYONEER_ACTIVITY_FIELDS = [
  ["activeMonths270d", "Active months 270d"], ["loaderCount180d", "Loader count 180d"], ["amazonVolume90d", "Amazon volume 90d"],
  ["walmartVolume90d", "Walmart volume 90d"], ["ebayVolume90d", "eBay volume 90d"], ["usdPsVolume90d", "USD PS volume 90d"],
  ["eurPsVolume90d", "EUR PS volume 90d"], ["priorOffers", "Prior offers"], ["knownPriorDelinquencies", "Known prior delinquencies"],
];

const lastCompletedMonths = (count = 6, referenceDate = new Date()) => Array.from({ length: count }, (_, index) => {
  const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - count + index, 1);
  return { ...EMPTY_MONTH, month: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}` };
});

const numeric = (value) => value === "" || value == null ? null : Number(value);
const money = (value) => value == null ? "—" : `$${Number(value).toLocaleString()}`;
const sentence = (value) => String(value || "").replaceAll("_", " ");
const isPdf = (file) => Boolean(file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name)));
const fieldSx = { minWidth: 0 };

function Field({ form, field, set, ...props }) {
  return <TextField size="small" fullWidth sx={fieldSx} value={form[field]} onChange={(event) => set(field, event.target.value)} {...props} />;
}

function FieldGrid({ children, columns = 2 }) {
  return <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` }, gap: 1.5 }}>{children}</Box>;
}

function ReviewSection({ title, rows }) {
  const populated = rows.filter(([, value]) => value !== "" && value != null);
  if (!populated.length) return null;
  return (
    <Box sx={{ p: 2, border: "1px solid #e3ebf3", borderRadius: 2.5 }}>
      <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: 1 }}>{title}</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
        {populated.map(([label, value]) => (
          <Box key={label}>
            <Typography sx={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ color: "#17233f", overflowWrap: "anywhere" }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function PayoneerFields({ fields, inputs, onChange }) {
  return <FieldGrid columns={3}>{fields.map(([field, label]) => <TextField key={field} size="small" fullWidth type="number" label={label} value={inputs[field]} inputProps={{ min: 0 }} onChange={(event) => onChange(field, event.target.value)} />)}</FieldGrid>;
}

function PayoneerRecommendations({ result, selectedProduct, onSelect }) {
  if (!result?.recommendations?.length) return null;
  return <Box sx={{ mt: 2 }}>
    <Typography sx={{ fontWeight: 900, color: "#0b2a4a", mb: 1 }}>CN/HK model recommendation</Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.25 }}>
      {result.recommendations.map((item) => <Box key={item.product} onClick={() => onSelect(item.product)} sx={{ p: 1.5, border: `2px solid ${selectedProduct === item.product ? "#1e6fe8" : "#e3ebf3"}`, borderRadius: 2.5, cursor: "pointer", bgcolor: selectedProduct === item.product ? "#f5f9ff" : "#fff" }}>
        <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>{item.product}</Typography>
        <Chip size="small" sx={{ my: .75 }} color={item.screeningStatus === "Model shortlist" ? "success" : item.screeningStatus === "Manual review" ? "warning" : "default"} label={item.screeningStatus} />
        <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#17233f" }}>{item.recommendedAdvanceUsd ? money(item.recommendedAdvanceUsd) : "No offer"}</Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>{item.predictedDelinquencyProbability == null ? "Outside v1 eligibility" : `${(item.predictedDelinquencyProbability * 100).toFixed(2)}% predicted delinquency · band ${item.riskBand}`}</Typography>
      </Box>)}
    </Box>
    <Alert severity="warning" sx={{ mt: 1.25 }}>Second-look research model. Every result requires final manual credit approval; parent-account volume is not used to size the entity offer.</Alert>
  </Box>;
}

export default function PaymentPlatformAddCompanyFlow({ tenantId, open, onClose, onCreated, onOpenReport }) {
  const isPayoneer = tenantId === PAYONEER_TENANT_ID;
  const [form, setForm] = useState(INITIAL_FORM);
  const [monthlyPerformance, setMonthlyPerformance] = useState(() => lastCompletedMonths());
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [bankBusy, setBankBusy] = useState(false);
  const [bankStatements, setBankStatements] = useState([]);
  const bankInput = useRef(null);
  const autofillInput = useRef(null);
  const [autofillText, setAutofillText] = useState("");
  const [autofillFile, setAutofillFile] = useState(null);
  const [autofillBusy, setAutofillBusy] = useState(false);
  const [autofillResult, setAutofillResult] = useState(null);
  const [payoneerQuery, setPayoneerQuery] = useState("");
  const [payoneerMatches, setPayoneerMatches] = useState([]);
  const [payoneerLookupBusy, setPayoneerLookupBusy] = useState(false);
  const [payoneerInputs, setPayoneerInputs] = useState(INITIAL_PAYONEER_INPUTS);
  const [payoneerBulkEntityId, setPayoneerBulkEntityId] = useState("");
  const [payoneerModelResult, setPayoneerModelResult] = useState(null);
  const [payoneerProduct, setPayoneerProduct] = useState("Grow");
  const [payoneerScoreBusy, setPayoneerScoreBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (open) return;
    setForm(INITIAL_FORM);
    setMonthlyPerformance(lastCompletedMonths());
    setStep(0);
    setResult(null);
    setError("");
    setBusy(false);
    setBankBusy(false);
    setBankStatements([]);
    setAutofillText("");
    setAutofillFile(null);
    setAutofillBusy(false);
    setAutofillResult(null);
    setPayoneerQuery("");
    setPayoneerMatches([]);
    setPayoneerLookupBusy(false);
    setPayoneerInputs(INITIAL_PAYONEER_INPUTS);
    setPayoneerBulkEntityId("");
    setPayoneerModelResult(null);
    setPayoneerProduct("Grow");
    setPayoneerScoreBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open || !isPayoneer || payoneerQuery.trim().length < 2 || payoneerBulkEntityId) {
      setPayoneerMatches([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setPayoneerLookupBusy(true);
      try {
        const matches = await lookupPayoneerMerchants(tenantId, payoneerQuery.trim(), 10);
        if (active) setPayoneerMatches(matches);
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || "The Payoneer bulk could not be searched.");
      } finally {
        if (active) setPayoneerLookupBusy(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [isPayoneer, open, payoneerBulkEntityId, payoneerQuery, tenantId]);

  const runPayoneerScore = async (inputs = payoneerInputs) => {
    setPayoneerScoreBusy(true);
    setError("");
    try {
      const scored = await scorePayoneerCnHkMerchant(tenantId, Object.fromEntries(Object.entries(inputs).map(([key, value]) => [key, key === "country" ? value : numeric(value) ?? 0])));
      setPayoneerModelResult(scored);
      const preferred = scored.recommendations.find((item) => item.screeningStatus === "Model shortlist")
        || scored.recommendations.find((item) => item.screeningStatus === "Manual review")
        || scored.recommendations.find((item) => item.product === "Grow")
        || scored.recommendations[0];
      if (preferred) setPayoneerProduct(preferred.product);
      return scored;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "The Payoneer CN/HK model could not score this merchant.");
      return null;
    } finally {
      setPayoneerScoreBusy(false);
    }
  };

  const selectPayoneerMerchant = async (merchant) => {
    const nextInputs = Object.fromEntries(Object.entries(merchant.modelInputs || {}).map(([key, value]) => [key, value == null ? "" : String(value)]));
    setForm((current) => ({ ...current, ...Object.fromEntries(Object.entries(merchant.identity || {}).filter(([, value]) => value != null).map(([key, value]) => [key, String(value)])) }));
    setPayoneerInputs({ ...INITIAL_PAYONEER_INPUTS, ...nextInputs });
    setPayoneerBulkEntityId(merchant.entityId);
    setPayoneerQuery(merchant.companyName);
    setPayoneerMatches([]);
    await runPayoneerScore({ ...INITIAL_PAYONEER_INPUTS, ...nextInputs });
    toast.success("Payoneer bulk data populated and CN/HK recommendation calculated");
  };

  const setPayoneerInput = (field, value) => {
    setPayoneerInputs((current) => ({ ...current, [field]: value }));
    setPayoneerModelResult(null);
  };

  const chooseAutofillFile = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { setError(`${file.name} exceeds the 15 MB limit.`); return; }
    if (!/\.(pdf|txt|csv|json|xlsx|xls)$/i.test(file.name)) {
      setError("Supported company data files are PDF, TXT, CSV, JSON, XLS, and XLSX.");
      return;
    }
    setError("");
    setAutofillFile(file);
  };

  const mergeAutofillMonths = (current, extracted = []) => {
    const rows = current.map((row) => ({ ...row }));
    const used = new Set();
    for (const source of extracted) {
      let index = rows.findIndex((row, rowIndex) => !used.has(rowIndex) && row.month === source.month);
      if (index < 0) index = rows.findIndex((row, rowIndex) => !used.has(rowIndex) && !row.paymentVolume && !row.transactions && !row.disputes);
      if (index < 0) continue;
      rows[index] = {
        ...rows[index],
        ...Object.fromEntries(Object.entries(source).map(([key, value]) => [key, value == null ? "" : String(value)])),
      };
      used.add(index);
    }
    return rows.sort((left, right) => String(left.month).localeCompare(String(right.month)));
  };

  const runAutofill = async () => {
    const pasted = autofillText.trim();
    if (!autofillFile && !pasted) { setError("Choose a company data file or paste company information first."); return; }
    setAutofillBusy(true);
    setAutofillResult(null);
    setError("");
    try {
      const result = await autofillPaymentPlatformAssessment(
        tenantId,
        autofillFile || pasted,
        autofillFile?.name || "pasted-company-data.txt",
      );
      const extractedFields = result?.fields || {};
      setForm((current) => ({
        ...current,
        ...Object.fromEntries(Object.entries(extractedFields)
          .filter(([, value]) => value !== null && value !== undefined && value !== "")
          .map(([key, value]) => [key, String(value)])),
      }));
      if (result?.monthlyPerformance?.length) {
        setMonthlyPerformance((current) => mergeAutofillMonths(current, result.monthlyPerformance));
      }
      let attachedStatement = null;
      let statementWarning = "";
      if (isPdf(autofillFile)) {
        try {
          attachedStatement = await analyzePaymentPlatformBankStatement(
            tenantId,
            autofillFile,
            String(extractedFields.companyName || form.companyName || "").trim(),
          );
          setBankStatements((current) => current.some((statement) =>
            statement.id === attachedStatement.id ||
            (statement.fileName === attachedStatement.fileName && statement.fileSize === attachedStatement.fileSize)
          ) ? current : [...current, attachedStatement]);
        } catch (statementError) {
          const status = statementError?.response?.status;
          if (![415, 422].includes(status)) {
            statementWarning = statementError?.response?.data?.message || "The PDF populated company fields, but its bank-statement evidence could not be attached.";
          }
        }
      }
      setAutofillResult({
        ...result,
        bankStatementAttached: Boolean(attachedStatement),
        warnings: [...(result?.warnings || []), ...(statementWarning ? [statementWarning] : [])],
      });
      toast.success(attachedStatement
        ? `${result?.populatedFieldCount || 0} company values populated and bank statement attached`
        : `${result?.populatedFieldCount || 0} company values populated by CrediArc AI`);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "CrediArc AI could not read this company data.");
    } finally {
      setAutofillBusy(false);
    }
  };

  const uploadBankStatements = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    const available = Math.max(0, 12 - bankStatements.length);
    if (!available) { setError("Up to 12 bank statements can be attached."); return; }
    const files = selected.slice(0, available);
    const invalid = files.find((file) => file.type !== "application/pdf" && !/\.pdf$/i.test(file.name));
    if (invalid) { setError("Only PDF bank statements are accepted."); return; }
    const oversized = files.find((file) => file.size > 25 * 1024 * 1024);
    if (oversized) { setError(`${oversized.name} exceeds the 25 MB limit.`); return; }
    setBankBusy(true);
    setError("");
    try {
      for (const file of files) {
        const statement = await analyzePaymentPlatformBankStatement(tenantId, file, form.companyName.trim());
        setBankStatements((current) => [...current, statement]);
      }
      toast.success(`${files.length} bank statement${files.length === 1 ? "" : "s"} analyzed`);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "The bank statement could not be analyzed.");
    } finally {
      setBankBusy(false);
    }
  };

  const monthlyError = useMemo(() => {
    const months = new Set();
    let turnoverMonths = 0;
    for (let index = 0; index < monthlyPerformance.length; index += 1) {
      const row = monthlyPerformance[index];
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(row.month)) return `Monthly row ${index + 1} needs a month.`;
      if (months.has(row.month)) return `Monthly history contains the month ${row.month} more than once.`;
      months.add(row.month);
      if (numeric(row.paymentVolume) != null) turnoverMonths += 1;
      if (MONTHLY_METRICS.some((field) => numeric(row[field]) != null && numeric(row[field]) < 0)) return "Monthly values cannot be negative.";
    }
    return turnoverMonths < 6 ? "Enter turnover for each of the six months." : "";
  }, [monthlyPerformance]);
  const stepError = step === 0 && !form.companyName.trim()
    ? "Enter the company name."
    : step === 0 && isPayoneer && !["CN", "HK"].includes(payoneerInputs.country)
      ? "Select CN or HK for the Payoneer model."
    : step === 0 && !isPayoneer && !form.address.trim()
      ? "Enter the operating address."
      : step === 0 && !isPayoneer && monthlyError
        ? monthlyError
        : step === 1 && numeric(form.requestedAmount) != null && numeric(form.requestedAmount) <= 0
          ? "Requested amount must be greater than zero when provided."
          : step === 1 && numeric(form.factorRate) != null && (numeric(form.factorRate) < 1 || numeric(form.factorRate) > 3)
            ? "Factor rate must be between 1.00 and 3.00 when provided."
            : step === 1 && numeric(form.remittanceRate) != null && (numeric(form.remittanceRate) <= 0 || numeric(form.remittanceRate) > 100)
              ? "Remittance rate must be greater than 0% and no more than 100% when provided."
          : "";
  const setMonthly = (index, key, value) => setMonthlyPerformance((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  const monthlyPayload = monthlyPerformance.map((row) => ({
    month: row.month,
    ...Object.fromEntries(MONTHLY_METRICS.map((field) => [field, numeric(row[field])]).filter(([, value]) => value != null)),
  }));
  const monthlySummary = useMemo(() => {
    const volumes = monthlyPerformance.map((row) => numeric(row.paymentVolume)).filter((value) => value != null);
    return {
      averageVolume: volumes.length ? volumes.reduce((total, value) => total + value, 0) / volumes.length : null,
      range: monthlyPerformance.map((row) => row.month).filter(Boolean).sort().filter((month, index, all) => index === 0 || index === all.length - 1).join(" – "),
    };
  }, [monthlyPerformance]);

  const next = () => {
    if (stepError) { setError(stepError); return; }
    setError("");
    setStep((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await createPaymentPlatformAssessment(tenantId, {
        identity: {
          externalId: form.externalId, applicationId: form.applicationId, companyName: form.companyName,
          website: form.website, registrationNumber: form.registrationNumber, email: form.email,
          address: form.address, city: form.city, state: form.state, zip: form.zip, country: isPayoneer ? payoneerInputs.country : undefined,
        },
        internalSignals: {
          fico: numeric(form.fico), businessRisk: numeric(form.businessRisk), businessMonths: numeric(form.businessMonths),
          averageMonthlyVolume: numeric(form.averageMonthlyVolume), disputes: numeric(form.disputes), transactions: numeric(form.transactions),
          logins: numeric(form.logins), productViews: numeric(form.productViews), feedbackScore: numeric(form.feedbackScore),
          feedbackCount: numeric(form.feedbackCount), entityConfidence: form.entityConfidence, legitimacy: form.legitimacy,
          externalRisk: form.externalRisk,
        },
        monthlyPerformance: monthlyPayload,
        requestedAmount: numeric(form.requestedAmount),
        commercialTerms: {
          factorRate: numeric(form.factorRate),
          remittanceRate: numeric(form.remittanceRate),
        },
        bankStatementUploadIds: bankStatements.map((statement) => statement.id),
        ...(isPayoneer ? {
          payoneerModelInputs: Object.fromEntries(Object.entries(payoneerInputs).map(([key, value]) => [key, key === "country" ? value : numeric(value) ?? 0])),
          payoneerBulkEntityId: payoneerBulkEntityId || null,
          payoneerProduct,
        } : {}),
      });
      onCreated?.(data);
      const entityId = data?.id || data?.entityId;
      const underwritingQueued = Boolean(data?.fullUnderwriting?.jobId);
      toast.success(underwritingQueued ? "Full underwriting started" : "Assessment saved; opening company report");
      if (entityId && onOpenReport) {
        onClose?.();
        onOpenReport(entityId);
        return;
      }
      setResult(data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "The assessment could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy && !bankBusy && !autofillBusy && !payoneerScoreBusy) onClose?.(); };
  const assessment = result?.assessment;
  const hasOffer =
    assessment?.decision !== "decline" &&
    assessment?.recommendation?.amount != null;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" aria-labelledby="mca-add-company-title">
      <DialogTitle id="mca-add-company-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#eaf2ff", color: "#1e6fe8", display: "grid", placeItems: "center" }}><AddBusinessIcon /></Box>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 900, color: "#0b2a4a" }}>Add company for MCA assessment</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>{isPayoneer ? "Search the Payoneer Google bulk by name or entity ID, review the populated CN/HK inputs, and get the model recommendation." : "Enter what you know. CrediArc resolves the entity, combines available evidence, and runs your MCA model."}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 475 }}>
        {!result && <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>{STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}</Stepper>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {(busy || bankBusy || autofillBusy || payoneerScoreBusy) && <Box sx={{ mb: 2 }}><LinearProgress /><Typography sx={{ color: "#64748b", fontSize: 13, mt: 1 }}>{payoneerScoreBusy ? "Running the Payoneer CN/HK model…" : autofillBusy ? (isPdf(autofillFile) ? "CrediArc AI is mapping company fields and checking the PDF for bank-statement evidence…" : "CrediArc AI is reading the supplied company data and mapping supported form fields…") : bankBusy ? "Reading every statement page and calculating underwriting cash-flow evidence…" : "Resolving identity, aggregating available evidence, running the MCA model, and queuing full underwriting…"}</Typography></Box>}

        {!result && step === 0 && <>
          {isPayoneer && <Box sx={{ mb: 2, p: 2, border: "1px solid #bdd4f5", borderRadius: 2.5, bgcolor: "#f5f9ff" }}>
            <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>Populate from Payoneer Google bulk</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1.25 }}>Type only the merchant name or entity ID. Select the match to populate every model input automatically.</Typography>
            <TextField fullWidth size="small" label="Merchant name or Entity ID" value={payoneerQuery} onChange={(event) => { setPayoneerQuery(event.target.value); setPayoneerBulkEntityId(""); }} InputProps={{ endAdornment: payoneerLookupBusy ? <CircularProgress size={17} /> : null }} />
            {!!payoneerMatches.length && <Box sx={{ mt: 1, bgcolor: "#fff", border: "1px solid #dbe5ef", borderRadius: 2, overflow: "hidden" }}>
              {payoneerMatches.map((merchant) => <Button key={merchant.entityId} fullWidth onClick={() => selectPayoneerMerchant(merchant)} sx={{ justifyContent: "space-between", px: 1.5, py: 1, borderRadius: 0, borderBottom: "1px solid #eef2f7", textTransform: "none" }}>
                <Box sx={{ textAlign: "left" }}><Typography sx={{ fontWeight: 800, color: "#17233f" }}>{merchant.companyName}</Typography><Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Entity {merchant.entityId} · {merchant.modelInputs?.country} · as of {merchant.storedRecommendation?.modelAsOfDate}</Typography></Box>
                <Chip size="small" label={merchant.storedRecommendation?.screeningStatus || "Bulk match"} />
              </Button>)}
            </Box>}
            {payoneerBulkEntityId && <Alert severity="success" sx={{ mt: 1.25 }}>Entity {payoneerBulkEntityId} matched. Identity and all CN/HK model inputs were populated from the Payoneer bulk.</Alert>}
          </Box>}
          {!isPayoneer && <Box sx={{ mb: 2, p: 2, border: "1px solid #bdd4f5", borderRadius: 2.5, bgcolor: "#f5f9ff" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesomeIcon sx={{ color: "#1e6fe8" }} />
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>Autofill with CrediArc AI</Typography>
                <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>Upload company data or paste text. CrediArc AI will populate supported fields across every step for your review.</Typography>
              </Box>
            </Box>
            <TextField
              multiline minRows={3} maxRows={7} fullWidth size="small"
              label="Paste company information"
              placeholder="Paste an application, CRM record, email, company profile, or structured data…"
              value={autofillText}
              disabled={autofillBusy}
              onChange={(event) => setAutofillText(event.target.value)}
              sx={{ mt: 1.5, bgcolor: "#fff" }}
            />
            <input ref={autofillInput} hidden type="file" accept=".pdf,.txt,.csv,.json,.xlsx,.xls,application/pdf,text/plain,text/csv,application/json,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={chooseAutofillFile} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
              <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={autofillBusy} onClick={() => autofillInput.current?.click()}>Choose company data file</Button>
              {autofillFile && <Chip size="small" label={autofillFile.name} onDelete={autofillBusy ? undefined : () => setAutofillFile(null)} />}
              <Button variant="contained" size="small" startIcon={autofillBusy ? <CircularProgress color="inherit" size={15} /> : <AutoAwesomeIcon />} disabled={autofillBusy || (!autofillFile && !autofillText.trim())} onClick={runAutofill}>
                {autofillBusy ? "Analyzing…" : "Analyze & populate form"}
              </Button>
            </Box>
            <Typography sx={{ color: "#64748b", fontSize: 11.5, mt: 1 }}>Supported: PDF, TXT, CSV, JSON, XLS, XLSX · maximum 15 MB. Existing values are replaced only when the source supports a field. Recognized PDF bank statements are analyzed and attached automatically.</Typography>
            {autofillResult && <Alert severity="success" sx={{ mt: 1.5 }}>
              <Typography sx={{ fontWeight: 800 }}>{autofillResult.populatedFieldCount} values populated. Review all steps before creating the assessment.</Typography>
              {autofillResult.summary && <Typography sx={{ fontSize: 12.5, mt: .3 }}>{autofillResult.summary}</Typography>}
              {autofillResult.bankStatementAttached && <Typography sx={{ fontSize: 12.5, mt: .3, fontWeight: 800 }}>Bank-statement evidence analyzed and attached.</Typography>}
              {!!autofillResult.warnings?.length && <Typography sx={{ fontSize: 12.5, mt: .3 }}>{autofillResult.warnings.join(" · ")}</Typography>}
            </Alert>}
          </Box>}
          <Alert severity="info" sx={{ mb: 2 }}>{isPayoneer ? "The model uses the exact fields from the CN/HK training files. Bulk values remain editable; re-run the model after any change." : "Company name, operating address, and six monthly turnover values are required. Everything else is optional and helps ArcAI refine its recommendation."}</Alert>
          <FieldGrid>
            <Field form={form} field="companyName" set={set} label="Legal or trading name" autoFocus required />
            <Field form={form} field="externalId" set={set} label="Merchant / account ID" />
            <Field form={form} field="registrationNumber" set={set} label="Registration number / EIN" />
            <Field form={form} field="applicationId" set={set} label="Application ID" />
            <Field form={form} field="website" set={set} label="Website" placeholder="example.com" />
            <Field form={form} field="email" set={set} label="Business email" type="email" />
            <Field form={form} field="address" set={set} label="Operating address" required={!isPayoneer} />
            <Field form={form} field="city" set={set} label="City" />
            <Field form={form} field="state" set={set} label="State / region" />
            <Field form={form} field="zip" set={set} label="ZIP / postal code" />
          </FieldGrid>
          {isPayoneer && <Box sx={{ mt: 2 }}><TextField select size="small" label="Country" value={payoneerInputs.country} onChange={(event) => setPayoneerInput("country", event.target.value)} sx={{ minWidth: 180 }}><MenuItem value="CN">China (CN)</MenuItem><MenuItem value="HK">Hong Kong (HK)</MenuItem></TextField><PayoneerRecommendations result={payoneerModelResult} selectedProduct={payoneerProduct} onSelect={setPayoneerProduct} /></Box>}
          {!isPayoneer && <><Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Last six months turnover</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 1.5 }}>Enter turnover month by month. Transactions and disputes are optional.</Typography>
          <Box sx={{ display: "grid", gap: 1.25 }}>
            {monthlyPerformance.map((row, index) => (
              <Box key={row.month || index} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.3fr 1fr 1fr" }, gap: 1 }}>
                <TextField size="small" type="month" label={`Month ${index + 1}`} value={row.month} onChange={(event) => setMonthly(index, "month", event.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField required size="small" type="number" label={`Monthly turnover ${index + 1}`} value={row.paymentVolume} onChange={(event) => setMonthly(index, "paymentVolume", event.target.value)} InputProps={{ startAdornment: <Box component="span" sx={{ mr: .5 }}>$</Box> }} />
                <TextField size="small" type="number" label={`Transactions ${index + 1}`} value={row.transactions} onChange={(event) => setMonthly(index, "transactions", event.target.value)} />
                <TextField size="small" type="number" label={`Disputes ${index + 1}`} value={row.disputes} onChange={(event) => setMonthly(index, "disputes", event.target.value)} />
              </Box>
            ))}
          </Box></>}
        </>}

        {!result && step === 1 && <>
          {isPayoneer ? <>
            <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Payoneer volume inputs</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>These labels and windows match the trained CN/HK input files. Values selected from the Google bulk can be reviewed and edited.</Typography>
            <PayoneerFields fields={PAYONEER_VOLUME_FIELDS} inputs={payoneerInputs} onChange={setPayoneerInput} />
          </> : <>
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Financing request</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>Requested amount is optional. If left empty, ArcAI will recommend the maximum supportable amount from its assessment.</Typography>
          <FieldGrid>
            <Field form={form} field="requestedAmount" set={set} label="Requested amount (optional)" type="number" InputProps={{ startAdornment: <Box component="span" sx={{ mr: .5 }}>$</Box> }} />
            <Field form={form} field="averageMonthlyVolume" set={set} label="Average monthly payment volume" type="number" InputProps={{ startAdornment: <Box component="span" sx={{ mr: .5 }}>$</Box> }} />
            <Field form={form} field="businessMonths" set={set} label="Months in business" type="number" />
            <Field form={form} field="fico" set={set} label="Principal FICO score" type="number" inputProps={{ min: 300, max: 850 }} />
            <Field form={form} field="businessRisk" set={set} label="Internal business risk (0–100)" type="number" inputProps={{ min: 0, max: 100 }} />
          </FieldGrid>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Commercial terms</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 1.5 }}>Optional values are locked when supplied: underwriting must use them and cannot replace them.</Typography>
          <FieldGrid>
            <Field form={form} field="factorRate" set={set} label="Factor rate (optional)" type="number" inputProps={{ min: 1, max: 3, step: .01 }} helperText="Locked when provided" />
            <Field form={form} field="remittanceRate" set={set} label="Remittance rate % (optional)" type="number" inputProps={{ min: .01, max: 100, step: .01 }} helperText="Locked when provided" />
          </FieldGrid>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Business bank statements</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 1.5 }}>Optional PDF statements are analyzed for deposits, balances, NSF/returned items, chargebacks, existing MCA remittances, debt service, and cash-flow risk. The results become underwriting evidence.</Typography>
          <input ref={bankInput} hidden multiple type="file" accept="application/pdf,.pdf" onChange={uploadBankStatements} />
          <Button variant="outlined" disabled={bankBusy || bankStatements.length >= 12} onClick={() => bankInput.current?.click()}>
            {bankBusy ? "Analyzing statements…" : "Add bank statements"}
          </Button>
          {!!bankStatements.length && (
            <Box sx={{ display: "grid", gap: 1, mt: 1.5 }}>
              {bankStatements.map((statement) => (
                <Box key={statement.id} sx={{ p: 1.5, border: "1px solid #e3ebf3", borderRadius: 2, display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                  <Box>
                    <Typography sx={{ color: "#0b2a4a", fontWeight: 800 }}>{statement.fileName}</Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                      {[statement.analysis?.bankName, statement.analysis?.statementMonth, statement.analysis?.depositAmount != null && `${money(statement.analysis.depositAmount)} deposits`].filter(Boolean).join(" · ")}
                    </Typography>
                    {statement.analysis?.summary && <Typography sx={{ color: "#475569", fontSize: 12, mt: .5 }}>{statement.analysis.summary}</Typography>}
                  </Box>
                  <Button size="small" color="inherit" disabled={bankBusy} onClick={() => setBankStatements((current) => current.filter((item) => item.id !== statement.id))}>Remove</Button>
                </Box>
              ))}
            </Box>
          )}
          </>}
        </>}

        {!result && step === 2 && <>
          {isPayoneer ? <>
            <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Payoneer activity and source inputs</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>Entity-level activity only. Parent-account activity is not counted toward collection capacity or offer size.</Typography>
            <PayoneerFields fields={PAYONEER_ACTIVITY_FIELDS} inputs={payoneerInputs} onChange={setPayoneerInput} />
            <Button sx={{ mt: 2 }} variant="contained" disabled={payoneerScoreBusy} onClick={() => runPayoneerScore()} startIcon={payoneerScoreBusy ? <CircularProgress color="inherit" size={15} /> : <AutoAwesomeIcon />}>{payoneerScoreBusy ? "Running model…" : "Run CN/HK model"}</Button>
            <PayoneerRecommendations result={payoneerModelResult} selectedProduct={payoneerProduct} onSelect={setPayoneerProduct} />
          </> : <>
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Payment-platform performance</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>All fields are optional and help CrediArc evaluate operating quality and engagement.</Typography>
          <FieldGrid columns={3}>
            <Field form={form} field="transactions" set={set} label="Transactions" type="number" />
            <Field form={form} field="disputes" set={set} label="Disputes / chargebacks" type="number" />
            <Field form={form} field="logins" set={set} label="Platform logins" type="number" />
            <Field form={form} field="productViews" set={set} label="Product views" type="number" />
            <Field form={form} field="feedbackScore" set={set} label="Feedback score" type="number" />
            <Field form={form} field="feedbackCount" set={set} label="Feedback count" type="number" />
            <Field select form={form} field="entityConfidence" set={set} label="Identity confidence"><MenuItem value="">Not provided</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Field>
            <Field select form={form} field="legitimacy" set={set} label="Business verification"><MenuItem value="">Not provided</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="corroborated">Corroborated</MenuItem><MenuItem value="unverified">Unverified</MenuItem></Field>
            <Field select form={form} field="externalRisk" set={set} label="Additional risk indicator"><MenuItem value="">Not provided</MenuItem><MenuItem value="low">Low</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="critical">Critical</MenuItem></Field>
          </FieldGrid>
          </>}
        </>}

        {!result && step === 3 && <Box sx={{ display: "grid", gap: 1.5 }}>
          <Alert severity="info">{isPayoneer ? "The selected product and current entity-level inputs will be saved with the CN/HK v1 recommendation. Final manual credit approval remains required." : "On assessment, CrediArc will match this entity, combine the submitted values with available merchant evidence, apply guardrails, and produce a recommendation."}</Alert>
          <ReviewSection title="Business" rows={[["Name", form.companyName], ["Merchant ID", form.externalId], ["Registration / EIN", form.registrationNumber], ["Website", form.website], ["Application", form.applicationId], ["Operating address", form.address], ["Location", [form.city, form.state, form.zip].filter(Boolean).join(", ")]]} />
          {isPayoneer ? <>
            <ReviewSection title="Selected model inputs" rows={[["Country", payoneerInputs.country], ["Account age", `${payoneerInputs.accountAgeDays || 0} days`], ["Eligible volume 90d", money(numeric(payoneerInputs.eligibleVolume90d) || 0)], ["Eligible volume 180d", money(numeric(payoneerInputs.eligibleVolume180d) || 0)], ["Active months 270d", payoneerInputs.activeMonths270d], ["Transactions 90d", payoneerInputs.transactions90d], ["Prior offers", payoneerInputs.priorOffers], ["Prior delinquencies", payoneerInputs.knownPriorDelinquencies]]} />
            {!payoneerModelResult && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => runPayoneerScore()}>Run model</Button>}>The inputs changed. Run the model before saving.</Alert>}
            <PayoneerRecommendations result={payoneerModelResult} selectedProduct={payoneerProduct} onSelect={setPayoneerProduct} />
          </> : <>
          <ReviewSection title="Application" rows={[["Requested", numeric(form.requestedAmount) == null ? "Maximum assessed amount" : money(numeric(form.requestedAmount))], ["Monthly volume", money(numeric(form.averageMonthlyVolume))], ["Months in business", form.businessMonths], ["FICO", form.fico], ["Internal risk", form.businessRisk]]} />
          <ReviewSection title="Performance" rows={[["Factor rate (locked)", form.factorRate], ["Remittance rate (locked)", form.remittanceRate === "" ? "" : `${form.remittanceRate}%`], ["Transactions", form.transactions], ["Disputes", form.disputes], ["Logins", form.logins], ["Product views", form.productViews], ["Identity confidence", form.entityConfidence], ["Verification", form.legitimacy]]} />
          <ReviewSection title="Monthly history" rows={[["Periods", monthlyPerformance.length], ["Range", monthlySummary.range], ["Average turnover", money(monthlySummary.averageVolume)]]} />
          <ReviewSection title="Bank statement evidence" rows={[["Statements analyzed", bankStatements.length || ""], ["Files", bankStatements.map((statement) => statement.fileName).join(", ")]]} />
          </>}
        </Box>}

        {result && <Box>
          <Box sx={{ textAlign: "center", py: 1 }}>
            <CheckCircleRoundedIcon sx={{ color: "#12a150", fontSize: 52 }} />
            <Typography sx={{ fontWeight: 900, fontSize: 24, color: "#0b2a4a" }}>Assessment saved</Typography>
            <Typography sx={{ color: "#64748b" }}>Full underwriting is running. Open the company report to follow its progress.</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
            {[["Decision", sentence(assessment?.decision)], ["Score", assessment?.score ?? "—"], ["Evidence", `${assessment?.coverage || 0}%`], ["Offer", hasOffer ? money(assessment?.recommendation?.amount) : "No offer"]].map(([label, value]) => <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f7f9fc" }}><Typography sx={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", fontWeight: 800 }}>{label}</Typography><Typography sx={{ color: "#0b2a4a", fontWeight: 900, fontSize: 19 }}>{value}</Typography></Box>)}
          </Box>
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {hasOffer && assessment?.recommendation?.termMonths && <Chip label={`${assessment.recommendation.termMonths} month term`} />}
            {hasOffer && assessment?.recommendation?.factorRate && <Chip label={`${assessment.recommendation.factorRate} factor rate`} />}
            <Chip label={`${sentence(assessment?.recommendation?.riskBand)} risk`} />
            {assessment?.recommendation?.requiresManualReview && <Chip color="warning" label="Manual review required" />}
            {result.fullUnderwriting?.jobId && <Chip color="primary" label="Full underwriting queued" />}
          </Box>
          {!!result.processingWarnings?.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>{result.processingWarnings.join(" ")}</Alert>
          )}
          {!hasOffer && <Alert severity="warning" sx={{ mt: 2 }}>No commercial offer was generated. Complete the required review before taking a funding action.</Alert>}
          {!!assessment?.missingFeatures?.length && <Alert severity="warning" sx={{ mt: 2 }}>Next step: collect {assessment.missingFeatures.slice(0, 3).map(sentence).join(", ")}{assessment.missingFeatures.length > 3 ? " and additional missing evidence" : ""} before re-running the assessment.</Alert>}
        </Box>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? <>
          <Button onClick={close}>Done</Button>
          <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpenReport?.(result.id || result.entityId)}>Open full report</Button>
        </> : <>
          <Button onClick={step === 0 ? close : () => { setError(""); setStep((current) => current - 1); }} startIcon={step > 0 ? <ArrowBackIcon /> : undefined}>{step === 0 ? "Cancel" : "Back"}</Button>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: "#64748b", fontSize: 12, mr: 1 }}>Step {step + 1} of {STEPS.length}</Typography>
          {step < STEPS.length - 1 ? <Button variant="contained" disabled={bankBusy || autofillBusy || payoneerScoreBusy} onClick={next} endIcon={<ArrowForwardIcon />}>Continue</Button> : <Button variant="contained" disabled={busy || bankBusy || autofillBusy || payoneerScoreBusy || (isPayoneer && !payoneerModelResult)} onClick={submit} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddBusinessIcon />}>{busy ? "Assessing…" : isPayoneer ? "Save recommendation" : "Aggregate & assess"}</Button>}
        </>}
      </DialogActions>
    </Dialog>
  );
}
